import React, { useEffect, useMemo, useState } from "react"
import PageHeader from "../../components/Ui/Page/PageHeader"
import { Container, OperationContainer } from "./OperationDashboard.styled"
import { useLazyQuery, useQuery } from "@apollo/client"
import SEARCH_ORDER_OPERATIONS from "../../graphql/queries/orderOperations/SearchOrderOperations"
import {
  CustomOperation,
  OrderOperationSearchHit,
  StoreGroup
} from "lib/types/generated/graphql-types"
import Operations from "../../components/OperationsDashboard/Operations"
import OperationsFilter from "../../components/OperationsDashboard/OperationsFilter"
import ErrorMessage from "../../components/Ui/Messages/ErrorMessage"
import ALL_STORE_GROUPS from "../../graphql/queries/store/AllStoreGroups"
import { Common } from "lib/types"
import { getCountryName } from "helpers/countries"
import { getOperationsQuery } from "../../components/OperationsDashboard/helpers"
import { Query } from "lib/types/common"
import PrimaryButton from "../../components/Ui/Buttons/PrimaryButton"
import FreeTrialBadge from "../../components/Ui/Messages/FreeTrialBadge"
import { ReactComponent as RefreshIcon } from "images/icons/rotate-right.svg"
import { OperationsOverTimeChart } from "../../components/OperationsDashboard/OperationChart/OperationsOverTimeChart"
import useOperationsOverTimeData from "../../components/OperationsDashboard/OperationChart/useOperationsOverTimeData"
import dayjs from "dayjs"

const OperationsDashboard = () => {
  const [createdOperations, setCreatedOperations] = useState<OrderOperationSearchHit[]>([])
  const [failedOperations, setFailedOperations] = useState<OrderOperationSearchHit[]>([])
  const [pendingOperations, setPendingOperations] = useState<OrderOperationSearchHit[]>([])

  const [filter, setFilter] = useState<Common.OrderOperationsFilter>({
    storeGroups: [],
    countryCodes: [],
    operations: [
      CustomOperation.Delivery,
      CustomOperation.Cancellation,
      CustomOperation.Refund,
      CustomOperation.Release,
      CustomOperation.Compensation,
      CustomOperation.ModifyOrderLines,
    ],
    startDate: dayjs().subtract(6, "days").format(),
    endDate: dayjs().format()
  })

  const {
    data: storeGroupData,
    loading: storeGroupLoading,
    error: storeGroupError
  } = useQuery(ALL_STORE_GROUPS)

  const [
    searchOperations,
    { loading: operationsLoading, error: operationsError, refetch: refetchOperationsQuery }
  ] = useLazyQuery(SEARCH_ORDER_OPERATIONS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setCreatedOperations(
        data?.searchOrderOperations?.hits.filter(
          (hit: OrderOperationSearchHit) => !hit.failed && !hit.started
        )
      )
      setFailedOperations(
        data?.searchOrderOperations?.hits.filter((hit: OrderOperationSearchHit) => hit.failed)
      )
      setPendingOperations(
        data?.searchOrderOperations?.hits.filter(
          (hit: OrderOperationSearchHit) =>
            hit.created && hit.started && !hit.completed && !hit.failed
        )
      )
    }
  })

  const storeGroups = storeGroupData?.getStoreGroups

  const getStoreGroupName = (storeGroupId: string) => {
    return storeGroups?.find((sg: StoreGroup) => sg.id === storeGroupId)?.name
  }

  const storeGroupOptions: Common.Option[] = useMemo(
    () =>
      storeGroups?.map((storeGroup: StoreGroup) => ({
        label: storeGroup.name,
        value: storeGroup.id
      })),
    [storeGroups]
  )

  const countryCodeOptions = () => {
    const codes =
      storeGroups?.flatMap(
        (storeGroup: StoreGroup) =>
          storeGroup.storeMarkets?.map((market) => market.countryCode) ?? []
      ) ?? []

    const uniqueCodes = [...new Set(codes)].filter(
      (code): code is string => typeof code === "string"
    )

    return uniqueCodes.map((countryCode) => ({
      label: getCountryName(countryCode) ?? "",
      value: countryCode
    }))
  }

  const search = () => {
    const query = getOperationsQuery(filter).toJSON() as Query
    searchOperations({
      variables: {
        query: JSON.stringify(query),
        from: 0,
        size: 1000,
        sort: [{ field: "created", order: "ASC" }],
        trackTotalHits: true
      }
    })
  }

  useEffect(() => {
    search()
  }, [filter])

  const memoizedStoreGroups = useMemo(
    () => filter.storeGroups.map((sg) => ({ value: sg, label: sg })),
    [filter.storeGroups]
  )

  const memoizedStoreMarkets = useMemo(
    () => filter.countryCodes.map((code) => ({ value: code, label: code })),
    [filter.countryCodes]
  )

  // Fetch chart data
  const { chartData, loading: chartLoading, error: chartError, refetch: refetchChart } = useOperationsOverTimeData({
    startDate: filter.startDate,
    endDate: filter.endDate,
    storeGroups: memoizedStoreGroups,
    storeMarkets: memoizedStoreMarkets,
    operations: filter.operations
  })

  const handleRefresh = () => {
    refetchOperationsQuery()
    refetchChart()
  }

  if (operationsError || storeGroupError)
    return <ErrorMessage message={"Unexpected error when fetching order operations"} />

  return (
    <>
      <PageHeader
        title={"Operations Dashboard"}
        description={"A summary of order operations, failed, pending and waiting for approval "}
      >
        <FreeTrialBadge untilDate={"2026-02-28"} />
        <PrimaryButton handleClick={handleRefresh}>
          <RefreshIcon />
          Refresh
        </PrimaryButton>
      </PageHeader>

      <OperationsFilter
        storeGroupOptions={storeGroupOptions}
        countryCodeOptions={countryCodeOptions()}
        setFilter={setFilter}
        filter={filter}
      />
      <OperationsOverTimeChart
        chartData={chartData}
        loading={chartLoading}
        error={chartError}
        operations={filter.operations}
        refetchLoading={operationsLoading}
      />
      <Container>
        <OperationContainer>
          <h2>Failed operations {failedOperations.length > 0 && <>({failedOperations.length})</>}</h2>
          <Operations
            hits={failedOperations}
            getStoreGroupName={getStoreGroupName}
            loading={operationsLoading || storeGroupLoading}
            refetchOperations={refetchOperationsQuery}
            setCreatedOperations={setCreatedOperations}
            setFailedOperations={setFailedOperations}
          />
        </OperationContainer>
        <OperationContainer>
          <h2>Approval needed {createdOperations.length > 0 && <>({createdOperations.length})</>}</h2>
          <Operations
            hits={createdOperations}
            setCreatedOperations={setCreatedOperations}
            getStoreGroupName={getStoreGroupName}
            loading={operationsLoading || storeGroupLoading}
            refetchOperations={refetchOperationsQuery}
            setFailedOperations={setFailedOperations}
          />
        </OperationContainer>
        {pendingOperations?.length > 0 && (
          <OperationContainer>
            <h2>Pending operations <>({pendingOperations.length})</></h2>
            <Operations
              hits={pendingOperations}
              getStoreGroupName={getStoreGroupName}
              loading={operationsLoading || storeGroupLoading}
              refetchOperations={refetchOperationsQuery}
              pending={true}
              setCreatedOperations={setCreatedOperations}
              setFailedOperations={setFailedOperations}
            />
          </OperationContainer>
        )}
      </Container>
    </>
  )
}

export default OperationsDashboard
